import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowClockwise, ArrowCounterClockwise, ArrowsClockwise, Funnel, Gauge, Plus, SpinnerGap, WarningCircle } from '@phosphor-icons/react';
import { AnsichtUmschalter, ImmoButton, ListenSuche, SchwebenderToast, useToastPill } from '../../../../components/immobilien/ui';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { mitViewTransition } from '../../../../lib/immobilien/viewTransition';
import {
  exportStatistik, filtereZaehler, istSortFeld, istStatusFilter, naechsteSortierung, sortiereZaehler, STANDARD_STATUS, STATUS_OPTIONEN, typOptionen,
  ZAEHLER_EXPORT_SPALTEN, type SortFeld, type SortRichtung, type StatusFilter,
} from '../../../../lib/zaehler/liste';
import { useZaehlerListe } from '../../../../lib/zaehler/useZaehlerListe';
import { FeldSelect } from '../teile';
import { ZaehlerKachel } from './ZaehlerKachel';
import { ZaehlerTabelle } from './ZaehlerTabelle';
import { ExportMenue } from './ExportMenue';
import ZaehlerFormular from '../ZaehlerFormular';
import type { ListenToast } from '../detail/ZaehlerSeite';

/**
 * Zählerliste (Handoff Zähler §5.1/§5.2). Suche, Filter, Sortierung und Ansicht stehen in der URL
 * (q, typ, status, sort, dir, view) – die Detailseite übernimmt die Parameter, „Alle Zähler“ kehrt dorthin zurück.
 */
export default function ZaehlerListeSeite() {
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const [params, setParams] = useSearchParams();
  const { mobil } = useMobilModus();
  const { zustand, neuLaden, laedtNeu } = useZaehlerListe();
  const [toast, zeige] = useToastPill();
  const [filterOffen, setFilterOffen] = useState(false);
  const [neuOffen, setNeuOffen] = useState(false);

  const suche = params.get('q') ?? '';
  const typ = params.get('typ') ?? '';
  const status: StatusFilter = istStatusFilter(params.get('status')) ? params.get('status') as StatusFilter : STANDARD_STATUS;
  const sortFeld: SortFeld = istSortFeld(params.get('sort')) ? params.get('sort') as SortFeld : 'nummer';
  const sortRichtung: SortRichtung = params.get('dir') === 'desc' ? 'desc' : 'asc';
  // mobil immer Kacheln (kein seitliches Scrollen)
  const ansicht = !mobil && params.get('view') === 'liste' ? 'liste' : 'kacheln';

  const setze = useCallback((werte: Record<string, string | null>, ersetzen = false) => {
    setParams(p => {
      const n = new URLSearchParams(p);
      for (const [k, v] of Object.entries(werte)) { if (v === null) n.delete(k); else n.set(k, v); }
      return n;
    }, { replace: ersetzen });
  }, [setParams]);

  const zeilen = zustand.status === 'ok' ? zustand.zeilen : null;
  const typen = useMemo(() => typOptionen(zeilen ?? []), [zeilen]);
  const sichtbar = useMemo(
    () => sortiereZaehler(filtereZaehler(zeilen ?? [], { suche, typ, status }), sortFeld, sortRichtung),
    [zeilen, suche, typ, status, sortFeld, sortRichtung],
  );
  const statistik = useMemo(() => exportStatistik(sichtbar, zeilen ?? []), [sichtbar, zeilen]);
  const filterGeaendert = !!typ || status !== STANDARD_STATUS;

  const oeffne = useCallback((id: string) => {
    mitViewTransition(() => {
      navigate(`/admin/zaehler/${encodeURIComponent(id)}${search}`);
      window.scrollTo(0, 0);
    });
  }, [navigate, search]);
  // Meldung der Detailseite (z. B. „Zähler gelöscht“) nach dem Zurückkehren zeigen
  useEffect(() => {
    const t = (state as { toast?: ListenToast } | null)?.toast;
    if (!t) return;
    zeige(t.text, t.fehler);
    navigate(`/admin/zaehler${search}`, { replace: true, state: null });
  }, [state, search, navigate, zeige]);

  const sortieren = (feld: SortFeld) => {
    const n = naechsteSortierung({ feld: sortFeld, richtung: sortRichtung }, feld);
    setze({ sort: n.feld === 'nummer' ? null : n.feld, dir: n.richtung === 'asc' ? null : 'desc' });
  };
  const zuruecksetzen = () => setze({ typ: null, status: null });

  // nach dem Erstellen bleibt die Liste stehen (wie bisher)
  const gespeichert = (meldung: string) => {
    zeige(meldung);
    setNeuOffen(false);
    neuLaden();
  };

  return (
    <>
      <header className="flex items-end justify-between flex-wrap" style={{ gap: 14 }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Verwaltung</span>
          <h1 className="m-0" style={{ fontSize: mobil ? 30 : 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05 }}>Zähler</h1>
          <span style={{ fontSize: 14, color: 'var(--ink2)' }}>{zeilen ? `${sichtbar.length} Zähler gefunden` : 'Wird geladen …'}</span>
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ExportMenue titel="Zähler" spalten={ZAEHLER_EXPORT_SPALTEN} ansicht={sichtbar} alle={async () => zeilen ?? []} statistik={statistik} onToast={zeige} />
          <ImmoButton groesse={44} pille={false} icon={laedtNeu ? SpinnerGap : ArrowsClockwise} title="Aktualisieren" aria-label="Aktualisieren"
            onClick={neuLaden} disabled={laedtNeu} />
          <ImmoButton art="akzent" groesse={44} pille={false} icon={Plus} onClick={() => setNeuOffen(true)} style={{ fontSize: 13, padding: '0 18px' }}>
            Neuer Zähler
          </ImmoButton>
        </div>
      </header>

      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <ListenSuche breit wert={suche} onChange={v => setze({ q: v || null }, true)} platzhalter="Zählernummer, Objekt, Standort..." ariaLabel="Zähler durchsuchen" />
        <ImmoButton art={filterOffen || filterGeaendert ? 'akzent' : 'glas'} groesse={44} icon={Funnel} aria-expanded={filterOffen} onClick={() => setFilterOffen(o => !o)}
          style={{ height: 46, borderRadius: 23, padding: '0 18px', fontSize: 13, fontWeight: 800 }}>
          Filter
        </ImmoButton>
        {!mobil && <AnsichtUmschalter hoehe={44} wert={ansicht} onChange={a => setze({ view: a === 'kacheln' ? null : a })} />}
      </div>

      {filterOffen && (
        <div className="immo-glas immo-side-in flex flex-wrap items-center" style={{ borderRadius: 22, padding: 14, gap: 10 }}>
          <FeldSelect ariaLabel="Typ" wert={typ} onChange={v => setze({ typ: v || null })} style={{ flex: '1 1 180px', maxWidth: 260 }}>
            <option value="">Alle Typen</option>
            {typen.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </FeldSelect>
          <FeldSelect ariaLabel="Status" wert={status} onChange={v => setze({ status: v === STANDARD_STATUS ? null : v })} style={{ flex: '1 1 160px', maxWidth: 220 }}>
            {STATUS_OPTIONEN.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </FeldSelect>
          {filterGeaendert && (
            <ImmoButton art="bad" groesse={40} icon={ArrowCounterClockwise} onClick={zuruecksetzen} style={{ fontSize: 12, fontWeight: 800 }}>Zurücksetzen</ImmoButton>
          )}
        </div>
      )}

      {zustand.status === 'fehler' && (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 26, padding: 20, gap: 14 }}>
          <WarningCircle weight="fill" size={28} color="var(--bad)" />
          <div className="flex flex-col flex-1" style={{ minWidth: 200 }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Zähler konnten nicht geladen werden</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{zustand.meldung}</span>
          </div>
          <ImmoButton icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>
        </div>
      )}

      {zustand.status === 'laedt' && (
        <div aria-busy="true" className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: '60px 20px', gap: 10 }}>
          <SpinnerGap size={26} color="var(--acc)" className="animate-spin" />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>Lade Zähler...</span>
        </div>
      )}

      {zeilen && sichtbar.length === 0 && (
        <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: '60px 20px', gap: 8 }}>
          <span className="flex items-center justify-center" style={{ width: 64, height: 64, borderRadius: 20, background: 'var(--fill2)', marginBottom: 6 }}>
            <Gauge weight="duotone" size={32} color="var(--ink3)" />
          </span>
          <span style={{ fontSize: 17, fontWeight: 800 }}>Keine Zähler gefunden</span>
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Erstellen Sie einen neuen Zähler</span>
        </div>
      )}

      {zeilen && sichtbar.length > 0 && ansicht === 'kacheln' && (
        <section aria-label="Zähler" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))', gap: 10 }}>
          {sichtbar.map(z => <ZaehlerKachel key={z.id} z={z} onOeffnen={oeffne} />)}
        </section>
      )}
      {zeilen && sichtbar.length > 0 && ansicht === 'liste' && (
        <ZaehlerTabelle zeilen={sichtbar} sortierung={{ feld: sortFeld, richtung: sortRichtung }} onSortieren={sortieren} onOeffnen={oeffne} />
      )}

      <SchwebenderToast toast={toast} mobil={mobil} />

      {neuOffen && (
        <ZaehlerFormular zaehler={null} onSchliessen={() => setNeuOffen(false)} onGespeichert={gespeichert} />
      )}
    </>
  );
}
